(() => {
const lifecycleKey = Symbol.for('skin-elite.scratchPopupLifecycle');
if (window[lifecycleKey]) {
  window[lifecycleKey].initialize(document);
  return;
}

const popupSelector = '[data-skin-elite-scratch-popup]';
const claimedKey = 'skin-eliteScratchPopupClaimed';
const submissionPendingKey = 'skin-eliteScratchPopupSubmissionPending';
const claimedCookie = 'skin-elite_scratch_claimed';
const dismissedKey = 'skin-eliteScratchPopupDismissedUntil';
const dismissedCookie = 'skin-elite_scratch_dismissed_until';
const dismissalSeconds = 7 * 24 * 60 * 60;
const popupInstances = new Map();
let dismissedUntil = 0;

function getDismissedUntil() {
  try {
    const stored = Number(localStorage.getItem(dismissedKey));
    if (Number.isFinite(stored) && stored > dismissedUntil) dismissedUntil = stored;
  } catch (_) {}
  try {
    const cookie = document.cookie.split(';').map((item) => item.trim())
      .find((item) => item.startsWith(`${dismissedCookie}=`));
    const stored = Number(cookie?.slice(dismissedCookie.length + 1));
    if (Number.isFinite(stored) && stored > dismissedUntil) dismissedUntil = stored;
  } catch (_) {}
  return dismissedUntil;
}

function persistDismissedState() {
  dismissedUntil = Date.now() + dismissalSeconds * 1000;
  try { localStorage.setItem(dismissedKey, String(dismissedUntil)); } catch (_) {}
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${dismissedCookie}=${dismissedUntil}; Max-Age=${dismissalSeconds}; Expires=${new Date(dismissedUntil).toUTCString()}; Path=/; SameSite=Lax${secure}`;
  } catch (_) {}
}

function hasClaimedCookie() {
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${claimedCookie}=`));
}

function persistClaimedState() {
  if (canUseStorage()) {
    localStorage.setItem(claimedKey, 'true');
  }

  document.cookie = `${claimedCookie}=1; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function canUseStorage() {
  try {
    localStorage.setItem('skin-eliteStorageTest', '1');
    localStorage.removeItem('skin-eliteStorageTest');
    return true;
  } catch {
    return false;
  }
}

function shouldShowPopup(popup) {
  if (popup.dataset.enabled !== 'true') return false;
  if (getDismissedUntil() > Date.now()) return false;
  if (hasClaimedCookie()) return false;
  if (!canUseStorage()) return true;
  if (localStorage.getItem(claimedKey) === 'true') return false;

  return true;
}

function onsiteKlaviyoCompanyId() {
  const script = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"][src*="company_id="]');
  if (!script) return '';
  try {
    return new URL(script.src).searchParams.get('company_id') || '';
  } catch {
    return '';
  }
}

async function identifyKlaviyoVisitor(publicKey, email, firstName) {
  const klaviyo = window.klaviyo;
  // Only identify when onsite tracking belongs to the same Klaviyo account as the list.
  if (typeof klaviyo?.identify !== 'function' || onsiteKlaviyoCompanyId() !== publicKey) return;

  try {
    await Promise.race([
      Promise.resolve(klaviyo.identify({ email, first_name: firstName || undefined })),
      new Promise((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  } catch (_) {}
}

async function subscribeToKlaviyo(popup, signupForm) {
  const publicKey = popup.dataset.klaviyoPublicKey;
  const listId = popup.dataset.klaviyoListId;
  const emailInput = signupForm.elements.namedItem('contact[email]');
  const firstNameInput = signupForm.elements.namedItem('contact[first_name]');
  const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
  const firstName = firstNameInput instanceof HTMLInputElement ? firstNameInput.value.trim() : '';

  if (!publicKey || !listId || !email) throw new Error('Missing Klaviyo subscription data.');

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12000);

  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'SKIN ELITE CO. scratch popup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    first_name: firstName || undefined,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED',
                        },
                      },
                    },
                    properties: {
                      'SKIN ELITE CO. Signup Source': 'Scratch popup',
                      'SKIN ELITE CO. Signup Page URL': window.location.href,
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        }),
        signal: controller.signal,
      },
    );

    if (!response.ok) throw new Error(`Klaviyo subscription failed with ${response.status}.`);
    await identifyKlaviyoVisitor(publicKey, email, firstName);
  } finally {
    window.clearTimeout(timeout);
  }
}

function initializeCanvas(canvas, reveal) {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return;

  const shell = canvas.parentElement;
  const width = shell?.clientWidth || 320;
  const height = shell?.clientHeight || 220;
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  context.scale(scale, scale);

  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#f0ca5e');
  gradient.addColorStop(0.46, '#c69d32');
  gradient.addColorStop(1, '#f1d275');
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  context.globalAlpha = 0.16;
  for (let index = 0; index < 520; index += 1) {
    const shade = index % 3 === 0 ? '#fff7c2' : '#6e5415';
    const radius = (index % 4) * 0.32 + 0.35;
    context.beginPath();
    context.fillStyle = shade;
    context.arc(Math.random() * width, Math.random() * height, radius, 0, Math.PI * 2);
    context.fill();
  }
  context.globalAlpha = 1;

  context.fillStyle = 'rgba(255, 249, 218, 0.92)';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = '800 29px Poppins, Arial, sans-serif';
  context.fillText('SCRATCH HERE', width / 2, height / 2 + 2);

  let scratching = false;
  let lastPoint;
  let scratchMoves = 0;

  function getPoint(event) {
    const box = canvas.getBoundingClientRect();
    return {
      x: event.clientX - box.left,
      y: event.clientY - box.top,
    };
  }

  function scratch(event) {
    if (!scratching) return;
    event.preventDefault();

    const point = getPoint(event);
    context.save();
    context.globalCompositeOperation = 'destination-out';
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 38;
    context.beginPath();
    context.moveTo(lastPoint?.x ?? point.x, lastPoint?.y ?? point.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    context.restore();
    lastPoint = point;
    scratchMoves += 1;

    if (scratchMoves >= 18) reveal();
  }

  canvas.addEventListener('pointerdown', (event) => {
    scratching = true;
    lastPoint = getPoint(event);
    canvas.setPointerCapture?.(event.pointerId);
    scratch(event);
  });
  canvas.addEventListener('pointermove', scratch);
  canvas.addEventListener('pointerup', () => {
    scratching = false;
    lastPoint = undefined;
  });
  canvas.addEventListener('pointercancel', () => {
    scratching = false;
    lastPoint = undefined;
  });
  canvas.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    reveal();
  });
}

function initializePopup(popup) {
  if (popupInstances.has(popup)) return;
  popup.dataset.initialized = 'true';
  const controller = new AbortController();
  const signal = controller.signal;
  const timers = new Set();

  function schedule(callback, delay) {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
  }

  function cancelTimers() {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
  }

  const dialog = popup.querySelector('.skin-elite-scratch-popup__dialog');
  const canvas = popup.querySelector('[data-scratch-canvas]');
  const claimButton = popup.querySelector('[data-scratch-claim]');
  const closeButtons = popup.querySelectorAll('[data-scratch-close]');
  const signupForm = popup.querySelector('.skin-elite-scratch-popup__form');
  const apiError = popup.querySelector('[data-skin-elite-scratch-api-error]');
  const submitButton = signupForm?.querySelector('button[type="submit"]');
  let returnFocus;
  let revealed = false;

  function showStep(name) {
    popup.querySelectorAll('[data-scratch-step]').forEach((step) => {
      const isActive = step.dataset.scratchStep === name;
      step.classList.toggle('is-active', isActive);
      step.setAttribute('aria-hidden', String(!isActive));
    });
  }

  function reveal() {
    if (revealed) return;
    revealed = true;
    canvas?.classList.add('is-revealed');
    if (claimButton instanceof HTMLButtonElement) {
      claimButton.hidden = false;
      schedule(() => claimButton.focus(), 280);
    }
  }

  function closePopup() {
    if (popup.hidden) return;
    persistDismissedState();
    cancelTimers();
    popup.hidden = true;
    document.body.classList.remove('skin-elite-scratch-popup-open');
    returnFocus?.focus?.();
  }

  function openPopup({ formResponse = false } = {}) {
    if (!popup.isConnected || popup.dataset.enabled !== 'true' || getDismissedUntil() > Date.now()) return;
    // Success/error responses may continue the form flow after a claim, but a
    // scheduled welcome popup must still be eligible when its timer fires.
    if (!formResponse && !shouldShowPopup(popup)) return;
    if (popup.hidden === false) return;
    returnFocus = document.activeElement;
    popup.hidden = false;
    document.body.classList.add('skin-elite-scratch-popup-open');
    dialog?.focus();
  }

  initializeCanvas(canvas, reveal);
  closeButtons.forEach((button) => button.addEventListener('click', closePopup, { signal }));
  claimButton?.addEventListener('click', () => {
    showStep('form');
    popup.querySelector('input[name="contact[first_name]"]')?.focus();
  }, { signal });

  signupForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!(signupForm instanceof HTMLFormElement)) return;
    if (!signupForm.checkValidity()) {
      signupForm.reportValidity();
      return;
    }
    if (signupForm.dataset.submitting === 'true') return;

    signupForm.dataset.submitting = 'true';
    popup.classList.add('is-submitting');
    popup.setAttribute('aria-busy', 'true');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;
    if (apiError instanceof HTMLElement) apiError.hidden = true;

    try {
      await subscribeToKlaviyo(popup, signupForm);
      persistClaimedState();
      if (canUseStorage()) localStorage.setItem(submissionPendingKey, 'true');
      HTMLFormElement.prototype.submit.call(signupForm);
    } catch {
      signupForm.dataset.submitting = 'false';
      popup.classList.remove('is-submitting');
      popup.removeAttribute('aria-busy');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      if (apiError instanceof HTMLElement) apiError.hidden = false;
    }
  }, { signal });

  popup.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePopup();
  }, { signal });

  popupInstances.set(popup, () => {
    cancelTimers();
    controller.abort();
    if (!popup.hidden) document.body.classList.remove('skin-elite-scratch-popup-open');
    popup.hidden = true;
    delete popup.dataset.initialized;
  });

  const submitted = Boolean(popup.querySelector('[data-skin-elite-scratch-result="success"]'));
  if (submitted) {
    const shouldShowSuccess = canUseStorage() && localStorage.getItem(submissionPendingKey) === 'true';
    persistClaimedState();
    if (canUseStorage()) {
      localStorage.removeItem(submissionPendingKey);
    }
    if (!shouldShowSuccess) return;
    showStep('success');
    schedule(() => openPopup({ formResponse: true }), 250);
    return;
  }

  const hasFormError = Boolean(popup.querySelector('[data-skin-elite-scratch-result="error"]'));
  if (hasFormError) {
    showStep('form');
    schedule(() => openPopup({ formResponse: true }), 250);
    return;
  }

  if (!shouldShowPopup(popup)) return;
  const delay = Number(popup.dataset.delay || 5000);
  schedule(openPopup, delay);
}

function initializeScratchPopups(root = document) {
  for (const [popup, destroy] of popupInstances) {
    if (!popup.isConnected) {
      destroy();
      popupInstances.delete(popup);
    }
  }
  if (root.matches?.(popupSelector)) initializePopup(root);
  root.querySelectorAll(popupSelector).forEach(initializePopup);
}

window[lifecycleKey] = { initialize: initializeScratchPopups };
initializeScratchPopups();
document.addEventListener('shopify:section:load', (event) => initializeScratchPopups(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const [popup, destroy] of popupInstances) {
    if (event.target === popup || event.target.contains?.(popup)) {
      destroy();
      popupInstances.delete(popup);
    }
  }
});
})();
